import React, { useMemo, useState, useCallback, useEffect } from 'react';
import AddRespondent from './addRespondent';
import EditRespondent from './editRespondent.js';
import Table from 'cccisd-table';
import Loader from 'cccisd-loader';
import _get from 'lodash/get';
import { client } from 'cccisd-apollo';
import notification from 'cccisd-notification';
import Modal from 'cccisd-modal';
import axios from 'cccisd-axios';
import copy from 'copy-to-clipboard';
import Tooltip from 'cccisd-tooltip';
import confirm from 'cccisd-confirm';
import IconLink from 'cccisd-icons/link';
import IconEdit from 'cccisd-icons/pencil5';
import IconArchive from 'cccisd-icons/box-add';
import IconUnarchive from 'cccisd-icons/box-remove';
import IconReload from 'cccisd-icons/loop3';
import classnames from 'classnames';
import style from './style.css';
import respondentsQuery from './respondentsQuery.graphql';

const Boilerplate = window.cccisd.boilerplate;

const Administer = ({ sites = [], selectedSiteId, onSiteChange, parentDeployments = [] }) => {
    const copyLinkRef = React.useRef(null);
    const editModalRefs = React.useRef({});

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const getParentDeploymentId = useCallback(
        siteId => {
            if (!siteId) {
                return null;
            }

            const Fortress = window.cccisd.fortress;
            const orgId = Fortress.user.acting.group_id;
            const deploymentHandle = `org_${orgId}_site_${siteId}`;
            return parentDeployments.find(d => d.deploymentHandle === deploymentHandle)?.deploymentId || null;
        },
        [parentDeployments]
    );

    const parentDeploymentId = useMemo(
        () => getParentDeploymentId(selectedSiteId),
        [getParentDeploymentId, selectedSiteId]
    );

    const parentDeployment = useMemo(
        () => parentDeployments.find(d => String(d.deploymentId) === String(parentDeploymentId)) || null,
        [parentDeployments, parentDeploymentId]
    );

    const getParentDeploymentGroupId = useCallback(
        deploymentId => {
            const deployment = parentDeployments.find(d => String(d.deploymentId) === String(deploymentId));
            return deployment?.assignment?.groupId || null;
        },
        [parentDeployments]
    );

    const parentDeploymentHash = parentDeployment?.hash || null;

    const fetchRespondents = useCallback(async () => {
        if (!parentDeploymentId) {
            setData([]);
            return;
        }

        setLoading(true);
        try {
            const response = await client.query({
                query: respondentsQuery,
                fetchPolicy: 'network-only',
                variables: {
                    parentDeploymentId: [Number(parentDeploymentId)],
                },
            });

            setData(_get(response, 'data.flows.deployment.respondentList', []));
        } catch (error) {
            setData([]);
            notification({
                type: 'danger',
                message: 'Unable to load respondents for selected site. Please try again.',
            });
        } finally {
            setLoading(false);
        }
    }, [parentDeploymentId]);

    useEffect(() => {
        fetchRespondents();
    }, [fetchRespondents]);

    const copyLink = surveyUrl => {
        copy(Boilerplate.url(surveyUrl));
        notification({
            message: 'Survey link copied successfully!',
            type: 'success',
            duration: 5000,
        });
    };

    const getSurveyUrl = row => {
        if (!parentDeploymentHash || !row.respondentHash) {
            return null;
        }

        const surveyUrl = `/d/${parentDeploymentHash}/${row.respondentHash}`;

        return (
            <Modal
                ref={copyLinkRef}
                size="medium"
                trigger={
                    <Tooltip title="Copy Survey Link">
                        <button type="button" className={classnames('btn', 'btn-sm', 'btn-success')}>
                            <IconLink />
                        </button>
                    </Tooltip>
                }
                title="Copy Survey Link"
            >
                <div>
                    <div>
                        <span>{Boilerplate.url(surveyUrl)}</span>
                    </div>
                    <br />
                    <div>
                        <button
                            type="button"
                            onClick={() => {
                                copyLink(surveyUrl);
                            }}
                            className={classnames('btn', 'btn-warning')}
                        >
                            <span>Copy survey link</span>
                        </button>
                    </div>
                </div>
            </Modal>
        );
    };

    const editRespondentAction = row => {
        return (
            <Modal
                ref={modalInstance => {
                    editModalRefs.current[row.pawnId] = modalInstance;
                }}
                size="small"
                trigger={
                    <Tooltip title="Edit Respondent">
                        <button type="button" className={`btn btn-sm btn-warning ${style.spaceLeft}`}>
                            <IconEdit />
                        </button>
                    </Tooltip>
                }
                title={`${row.username}`}
            >
                <EditRespondent
                    row={row}
                    closeModal={() => editModalRefs.current[row.pawnId] && editModalRefs.current[row.pawnId].close()}
                    onSuccess={fetchRespondents}
                />
            </Modal>
        );
    };

    const archiveRespondent = async row => {
        const response = await axios.put(
            Boilerplate.route(`api.nexus.pawn.archive`, {
                pawn: row.pawnId,
            }),
            {
                pawnHash: row.pawnHash,
            }
        );

        if (response.data.errors && response.data.errors.pawn) {
            notification({
                type: 'danger',
                message: `${row.username} failed to archive`,
            });
        }

        if (response.data.status === 'success') {
            notification({
                message: `${row.username} successfully archived`,
                type: 'success',
                duration: 8000,
            });

            fetchRespondents();
        }
    };

    const archiveRespondentAction = row => {
        return (
            <Tooltip title="Archive Respondent">
                <a
                    className="btn btn-sm btn-primary"
                    onClick={() => {
                        confirm({
                            message: 'Confirm archive?',
                            description: 'Are you sure you want to archive ' + row.username + "'?",
                            confirmLabel: 'Yes',
                            abortLabel: 'Cancel',
                        }).then(() => archiveRespondent(row));
                    }}
                >
                    <IconArchive />
                </a>
            </Tooltip>
        );
    };

    const unarchiveRespondent = async row => {
        const response = await axios.put(
            Boilerplate.route(`api.nexus.pawn.unarchive`, {
                pawn: row.pawnId,
            }),
            {
                pawnHash: row.pawnHash,
            }
        );

        if (response.data.errors && response.data.errors.pawn) {
            notification({
                type: 'danger',
                message: `${row.username} failed to unarchive`,
            });
        }

        if (response.data.status === 'success') {
            notification({
                message: `${row.username} successfully unarchived`,
                type: 'success',
                duration: 8000,
            });

            fetchRespondents();
        }
    };

    const unarchiveRespondentAction = row => {
        return (
            <Tooltip title="Unarchive Respondent">
                <a
                    className="btn btn-sm btn-info"
                    onClick={() => {
                        confirm({
                            message: 'Confirm unarchive?',
                            description:
                                'Are you sure you want to unarchive ' +
                                row.username +
                                "'? This user was archived on " +
                                new Date(row.archivedAt).toLocaleDateString() +
                                ' and unarchiving will make them active again.',
                            confirmLabel: 'Yes',
                            abortLabel: 'Cancel',
                        }).then(() => unarchiveRespondent(row));
                    }}
                >
                    <IconUnarchive />
                </a>
            </Tooltip>
        );
    };

    const getColumns = () => [
        {
            handle: 'actions',
            label: 'Actions',
            name: 'actions',
            render: (value, row) => {
                const actionButtons = row.archived
                    ? [unarchiveRespondentAction(row)]
                    : [getSurveyUrl(row), editRespondentAction(row), archiveRespondentAction(row)].filter(Boolean);

                return <div className={style.actionButtons}>{actionButtons}</div>;
            },
            renderCSV: (value, row) => {
                const surveyUrl = `/d/${parentDeploymentHash}/${row.respondentHash}`;
                return Boilerplate.url(surveyUrl);
            },
        },
        {
            handle: 'username',
            label: 'Unique Identifier',
            name: 'username',
            sort: true,
            filter: true,
        },
        {
            handle: 'firstName',
            label: 'First Name',
            name: 'firstName',
            sort: true,
            filter: true,
        },
        {
            handle: 'lastName',
            label: 'Last Name',
            name: 'lastName',
            sort: true,
            filter: true,
        },
    ];

    const tableData = Array.isArray(data)
        ? data.map(row => ({
              deploymentId: _get(row, 'deployment.deploymentId', null),
              pawnId: row.pawnId,
              username: _get(row, 'pawn.user.username', ''),
              firstName: _get(row, 'pawn.user.firstName', ''),
              lastName: _get(row, 'pawn.user.lastName', ''),
              respondentHash: _get(row, 'pawn.pawn.respondentHash', ''),
              pawnHash: _get(row, 'pawn.pawn.pawnHash', ''),
              passcode: row.passcode,
              archived: _get(row, 'pawn.pawn.archived', false),
              archivedAt: _get(row, 'pawn.pawn.archivedAt', null),
          }))
        : [];

    if (loading) {
        return <Loader loading type="inline" />;
    }

    return (
        <>
            <div
                className="d-flex justify-content-between align-items-end"
                style={{ gap: '20px', marginBottom: '50px' }}
            >
                <div className="form-group" style={{ flex: '0 1 320px', maxWidth: '320px', marginBottom: 0 }}>
                    <label htmlFor="administer-site-select">Select Site:</label>
                    <select
                        id="administer-site-select"
                        className="form-select"
                        onChange={event => onSiteChange(event.target.value)}
                        value={selectedSiteId}
                    >
                        <option value="">Select a site</option>
                        {sites.map(site => (
                            <option key={site.group.groupId} value={site.group.groupId}>
                                {site.group.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div
                    style={{
                        flex: '0 0 auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                    }}
                >
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={fetchRespondents}
                        disabled={!selectedSiteId || !parentDeploymentId || loading}
                    >
                        <IconReload /> Reload Respondents
                    </button>

                    <AddRespondent
                        deployment={
                            parentDeploymentId
                                ? {
                                      deploymentId: parentDeploymentId,
                                      groupId: getParentDeploymentGroupId(parentDeploymentId),
                                  }
                                : null
                        }
                        disabled={!selectedSiteId || !parentDeploymentId}
                        onSubmitSuccess={fetchRespondents}
                    />
                </div>
            </div>
            <Table
                rowKey="pawnId"
                name="open-links-table"
                columns={getColumns()}
                data={tableData}
                showPerPageOptions
                perPageOptions={[10, 25, 50, 100]}
                perPage={10}
                isCsvDownload
                csvFilename="respondents.csv"
            />
        </>
    );
};

export default Administer;
